import { useEffect } from 'react';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { parseProducts, parseProduct, parseApiResponse, type RawProduct } from '../utils/productParser';

type ApiResponse = {
  success: boolean;
  data: RawProduct[];
};

interface UsePlatformDataOptions {
  selectedPlatform: string | null;
  currentView: string;
  browseMode: boolean;
  selectedCountry: string;
  selectedRegion: string;
}

/**
 * Fetches platform products (Level 1, Level 2, Integrated Payments, Shift4 Payments,
 * Shift4 Customer Hub) whenever the platform/view/filters change, and writes results
 * directly into ProductMatrixContext.
 */
export function usePlatformData({
  selectedPlatform,
  currentView,
  browseMode,
  selectedCountry,
  selectedRegion,
}: UsePlatformDataOptions): void {
  const { setPlatformProducts, setPlatformLoading, setLbm2Products, setTabSearchQuery } =
    useProductMatrix();

  useEffect(() => {
    // ── Main platform products ─────────────────────────────────────────────
    const fetchPlatformProducts = async () => {
      if (!selectedPlatform || currentView !== 'platform') return;

      setTabSearchQuery('');
      setPlatformLoading(true);

      try {
        // Level 1
        const params = new URLSearchParams();
        params.append('platform', selectedPlatform);
        if (!browseMode) {
          if (selectedCountry) params.append('country', selectedCountry);
          if (selectedRegion) params.append('region', selectedRegion);
        }
        const [mainResponse, level2Response] = await Promise.all([
          fetch(`/api/products?${params.toString()}`),
          (async () => {
            const lp = new URLSearchParams();
            lp.append('solution_level', '2');
            if (!browseMode) {
              if (selectedCountry) lp.append('country', selectedCountry);
              if (selectedRegion) lp.append('region', selectedRegion);
            }
            return fetch(`/api/products?${lp.toString()}`);
          })(),
        ]);

        const [data, level2Data] = await Promise.all([
          mainResponse.json(),
          level2Response.json(),
        ]);

        // Integrated Payments — only for Shift4 Duo
        let integratedPaymentsData: ApiResponse | null = null;
        if (selectedPlatform === 'Shift4 Duo') {
          const ip = new URLSearchParams();
          ip.append('platform', 'Integrated Payments');
          const r = await fetch(`/api/products?${ip.toString()}`);
          integratedPaymentsData = parseApiResponse(await r.json());
        }

        // Shift4 Payments — for Dine / Venue / One / Duo
        let shift4PaymentsData: ApiResponse | null = null;
        if (
          selectedPlatform === 'Shift4 Dine' ||
          selectedPlatform === 'Shift4 Venue' ||
          selectedPlatform === 'Shift4 One' ||
          selectedPlatform === 'Shift4 Duo'
        ) {
          const sp = new URLSearchParams();
          sp.append('platform', 'Payment Methods');
          const r = await fetch(`/api/products?${sp.toString()}`);
          shift4PaymentsData = parseApiResponse(await r.json());
        }

        if (data.success) {
          let allRaw: RawProduct[] = data.data || [];

          if (level2Data.success) {
            const existingKeys = new Set(
              allRaw.map((p: RawProduct) => `${p.platform}|${p.solution}|${p.solution_level}`)
            );
            const deduped = level2Data.data.filter(
              (p: RawProduct) => !existingKeys.has(`${p.platform}|${p.solution}|${p.solution_level}`)
            );
            allRaw = [...allRaw, ...deduped];
          }

          if (integratedPaymentsData?.success) {
            const patt = integratedPaymentsData.data.filter(
              (p: RawProduct) => typeof p.patt === 'string' && p.patt.trim() !== ''
            );
            allRaw = [...allRaw, ...patt];
          }

          if (shift4PaymentsData?.success) {
            allRaw = [...allRaw, ...shift4PaymentsData.data];
          }

          setPlatformProducts(parseProducts(allRaw));
        }
      } catch (error) {
        console.error('Error fetching platform products:', error);
        setPlatformProducts([]);
      } finally {
        setPlatformLoading(false);
      }
    };

    // ── Shift4 Customer Hub (lbm2) ─────────────────────────────────────────
    const fetchLbm2Products = async () => {
      if (selectedPlatform !== 'Shift4 Dine' || currentView !== 'platform') {
        setLbm2Products([]);
        return;
      }

      try {
        const params = new URLSearchParams();
        params.append('platform', 'Shift4 Customer Hub');
        const response = await fetch(`/api/products?${params.toString()}`);
        const data = await response.json();

        if (data.success) {
          setLbm2Products(data.data.map(parseProduct));
        }
      } catch (error) {
        console.error('Error fetching Shift4 Customer Hub products:', error);
        setLbm2Products([]);
      }
    };

    fetchPlatformProducts();
    fetchLbm2Products();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, currentView, browseMode, selectedCountry, selectedRegion]);
}
